import React from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

/**
 * ComparisonChart - A reusable component for dual-axis comparison charts
 * 
 * @param {Object} props
 * @param {Array} props.data - Array of data objects to display
 * @param {string} props.title - Chart title
 * @param {string} props.xAxisKey - Key for X-axis labels (e.g., 'name', 'ad_type')
 * @param {Array} props.metrics - Array of metric configurations
 *   Example: [
 *     { key: 'spend', label: 'Spend', color: '#8b5cf6', format: 'currency', yAxisId: 'left' },
 *     { key: 'roas', label: 'ROAS', color: '#ec4899', format: 'number', yAxisId: 'right' }
 *   ]
 * @param {number} props.height - Chart height (default: 400)
 * @param {number} props.maxItems - Maximum number of items to display (default: 10)
 */
const ComparisonChart = ({ 
  data = [], 
  title, 
  xAxisKey = 'name',
  metrics = [],
  height = 400,
  maxItems = 10
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">{title || 'Comparison Chart'}</h3>
        <p className="text-gray-500 text-center py-8">No data available for comparison</p>
      </div>
    );
  }

  // Format value based on format type
  const formatValue = (value, format) => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    
    switch (format) {
      case 'currency':
        return `$${parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      case 'percent':
        return `${parseFloat(value).toFixed(2)}%`;
      case 'number':
        return parseFloat(value).toFixed(2);
      default:
        return value;
    }
  };

  // Prepare data - limit to maxItems and ensure proper formatting
  const chartData = data.slice(0, maxItems).map(item => {
    const formattedItem = { ...item };
    // Ensure all metric keys are numbers for proper chart rendering
    metrics.forEach(metric => {
      if (formattedItem[metric.key] !== undefined) {
        formattedItem[metric.key] = parseFloat(formattedItem[metric.key]) || 0;
      }
    });
    return formattedItem;
  });

  // Separate metrics by yAxisId
  const leftAxisMetrics = metrics.filter(m => m.yAxisId === 'left' || !m.yAxisId);
  const rightAxisMetrics = metrics.filter(m => m.yAxisId === 'right');

  // Custom tooltip formatter
  const customTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-800 mb-2">{label}</p>
          {payload.map((entry, index) => {
            const metric = metrics.find(m => m.key === entry.dataKey);
            return (
              <p key={index} className="text-sm" style={{ color: entry.color }}>
                {metric?.label || entry.dataKey}: {formatValue(entry.value, metric?.format || 'number')}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">{title || 'Comparison Chart'}</h3>
      <div style={{ width: '100%', height: `${height}px`, minHeight: '400px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis 
            dataKey={xAxisKey}
            angle={-45}
            textAnchor="end"
            height={100}
            tick={{ fontSize: 12 }}
            interval={0}
          />
          {leftAxisMetrics.length > 0 && (
            <YAxis 
              yAxisId="left"
              orientation="left"
              tick={{ fontSize: 12 }}
              label={{ value: leftAxisMetrics[0]?.label || '', angle: -90, position: 'insideLeft' }}
            />
          )}
          {rightAxisMetrics.length > 0 && (
            <YAxis 
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 12 }}
              label={{ value: rightAxisMetrics[0]?.label || '', angle: 90, position: 'insideRight' }}
            />
          )}
          <Tooltip content={customTooltip} />
          <Legend 
            wrapperStyle={{ paddingTop: '20px' }}
            formatter={(value) => {
              const metric = metrics.find(m => m.key === value || m.label === value);
              return metric?.label || value;
            }}
          />
          {metrics.map((metric, index) => (
            <Bar
              key={index}
              yAxisId={metric.yAxisId || 'left'}
              dataKey={metric.key}
              name={metric.label}
              fill={metric.color || '#8b5cf6'}
              radius={[4, 4, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ComparisonChart;

